import React from "react";
import { Form, FormSpy } from "react-final-form";
import { useSelector } from "react-redux";
import { Segment } from "@mui/icons-material";
import { Box, FormLabel, TableContainer } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import classNames from "classnames";
import { FormApi } from "final-form";

import { selectAllFolders, selectProjectsWithinAccount, useGetProjectsQuery } from "fond/api";
import { CheckboxField, ProjectVersionField } from "fond/form/fields";
import { useEditorContext } from "fond/styleEditor/Editor/EditorProvider";
import { BaseMultiProject, Maybe, Project, Version } from "fond/types";
import { LayerStyle } from "fond/types/ProjectLayerConfig";
import { required } from "fond/utils/validation";
import { NonIdealState } from "fond/widgets";

import LayerStyleTree from "./LayerStyleTree";

export interface ICopyStylesFormData {
  selectedLayerStyles?: LayerStyle[];
  OverwriteStyles?: boolean;
  Project: Maybe<Project | BaseMultiProject>;
  Version: Maybe<Version>;
}

interface IProps {
  initialValues: Partial<ICopyStylesFormData>;
  isSubmitting?: boolean;
  onChange(values: ICopyStylesFormData): void;
}

const CopyStylesForm: React.FC<IProps> = ({ initialValues, isSubmitting = false, onChange }: IProps) => {
  useGetProjectsQuery(undefined);
  const projects = useSelector(selectProjectsWithinAccount);
  const folders = useSelector(selectAllFolders);
  const emptyTheme = useTheme();
  const { setCopyStylesSourceProject, setCopyStylesSourceVersion } = useEditorContext();

  const onFormUpdate = (values: ICopyStylesFormData, _: FormApi<ICopyStylesFormData>) => {
    // Update context when form values change
    setCopyStylesSourceProject(values.Project);
    setCopyStylesSourceVersion(values.Version);
    onChange(values);
  };

  return (
    <Form<ICopyStylesFormData>
      initialValues={initialValues}
      onSubmit={onFormUpdate}
      render={({ handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Box>
              <Box>
                <ProjectVersionField
                  projects={projects}
                  folders={folders}
                  projectValidator={required}
                  versionValidator={required}
                  disabled={isSubmitting}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <FormLabel>Layers</FormLabel>
                <FormSpy subscription={{ values: true, invalid: true }}>
                  {({ values }) => (
                    <TableContainer
                      className={classNames("customScrollbars")}
                      sx={{ height: 300, overflow: "auto", mt: 2, borderRadius: 1, border: "1px solid #ddddde" }}
                    >
                      {values.Version?.ID ? (
                        <LayerStyleTree
                          versionId={values.Version?.ID}
                          onSelectionChange={(selected: LayerStyle[]) => {
                            onChange({ ...values, selectedLayerStyles: selected } as ICopyStylesFormData);
                          }}
                        />
                      ) : (
                        <NonIdealState
                          title="Nothing to show..."
                          description="Select a project to copy styles from."
                          icon={<Segment sx={{ fill: emptyTheme.palette.biarri.primary.lightGrey }} />}
                          size="small"
                        />
                      )}
                    </TableContainer>
                  )}
                </FormSpy>
              </Box>
              <Box sx={{ mt: 2 }}>
                <CheckboxField name="OverwriteStyles" label="Overwrite existing styles" />
              </Box>
              <FormSpy
                subscription={{ values: true, valid: true, pristine: true }}
                onChange={({ valid, pristine }) => {
                  if (valid && !pristine) handleSubmit();
                }}
              />
            </Box>
          </form>
        );
      }}
    />
  );
};

export default CopyStylesForm;
